/**
 * Custom hook for memoized coverage calculations
 * Story 280: More reliably view stories in the workspace
 *
 * Computes which upstream anchors are covered by sections and which are uncovered.
 * Memoizes the results to avoid O(n*m) recalculation on every render.
 */

import { useMemo } from 'react';
import type { DocumentSection } from '../types/document';

interface CoverageResult {
  /** Set of upstream anchors that are covered by at least one section */
  coveredAnchors: Set<string>;
  /** Set of upstream anchors that are not covered by any section */
  uncoveredAnchors: Set<string>;
}

/**
 * Computes coverage sets for document sections
 *
 * @param sections - Document sections that may cover upstream anchors
 * @param upstreamAnchors - List of upstream anchors to check coverage against
 * @returns Memoized coverage result with covered and uncovered anchor sets
 */
export function useCoverageCalculations(
  sections: DocumentSection[],
  upstreamAnchors: string[]
): CoverageResult {
  // Memoize covered anchors set - O(n) where n is total covers across sections
  const coveredAnchors = useMemo(() => {
    const covered = new Set<string>();
    for (const section of sections) {
      if (section.covers) {
        for (const anchor of section.covers) {
          covered.add(anchor);
        }
      }
    }
    return covered;
  }, [sections]);

  // Memoize uncovered anchors set - O(m) where m is upstream anchors count
  const uncoveredAnchors = useMemo(() => {
    return new Set(upstreamAnchors.filter((a) => !coveredAnchors.has(a)));
  }, [upstreamAnchors, coveredAnchors]);

  return { coveredAnchors, uncoveredAnchors };
}
